import LSP6Schema from '@erc725/erc725.js/schemas/LSP6KeyManager.json'
import ERC725, { ERC725JSONSchema } from '@erc725/erc725.js'
import Web3 from 'web3'
import { LSP1_TYPE_IDS } from '@lukso/lsp-smart-contracts'

import {
  ContractCreateComponent,
  DefaultTxComponents,
  SET_DATA_BATCH_COMPONENT_NAME,
  SET_DATA_COMPONENT_NAME,
  Transaction,
  TransactionComponent,
  TransactionStatus,
} from '@/shared/models/Transaction'
import { SMART_CONTRACT_CONFIG } from './SmartContractConfig'
import {
  SETDATA_BATCH_ERC725Y,
  SETDATA_ERC725Y,
} from '@/transactionConfirmationSelectors/functionSignatures'
import { ZERO_ADDRESS } from '@/shared/models/Profile'
import { ContractEventType } from '@/shared/models/ContractEvent'

/**
 * Checks if a transaction is of type set data.
 *
 * @param TransactionComponent[] transaction components
 * @returns boolean
 */
export const isSetDataTransaction = (
  components?: TransactionComponent[]
): boolean => {
  if (components && components.length > 0) {
    const componentName = components.at(-1)?.component
    return (
      componentName === SET_DATA_COMPONENT_NAME ||
      componentName === SET_DATA_BATCH_COMPONENT_NAME ||
      componentName === DefaultTxComponents.SET_DATA
    )
  }

  return false
}

export const isUPExecuteSignature = (data: string): boolean => {
  return data.startsWith(SMART_CONTRACT_CONFIG.upExecuteHex)
  // executeBatch cannot be properly visualised yet when decoded so we do not check for it
  //data.startsWith(SMART_CONTRACT_CONFIG.upExecuteBatchHex)
}

/**
 * Check if transaction data starts with setData signature
 *
 * @param data - transaction data
 * @returns boolean
 */
export const isSetDataSignature = (data: string): boolean => {
  return (
    data.startsWith(SMART_CONTRACT_CONFIG.setDataHex) ||
    data.startsWith(SMART_CONTRACT_CONFIG.setDataBatchHex)
  )
}

const isLSP6DataKey = (dataKeyName: string | undefined) => {
  return (
    dataKeyName != undefined && dataKeyName.startsWith('AddressPermissions')
  )
}

/**
 * Check if a given transaction has the effect of setting any data keys related to LSP6 Permissions.
 * Such data keys include:
 *
 * - `AddressPermissions[]` (list of controllers)
 * - `AddressPermissions:Permissions:<controller>`
 * - `AddressPermissions:AllowedCalls:<controller>`
 * - `AddressPermissions:AllowedERC725YDataKeys:<controller>`
 *
 * @param transaction A Transaction component
 * @param web3 A web3 instance to decode the parameters
 * @returns `true` if the transaction is related to setting permissions, `false` otherwise.
 */
export const isSettingPermissions = (
  transaction: Transaction,
  web3: Web3
): boolean => {
  if (!transaction.components || transaction.components?.length == 0)
    return false

  for (const [, component] of Object.entries(transaction.components)) {
    if (component.functionSignature == SETDATA_BATCH_ERC725Y) {
      const schemaOfDataKey = getDataKeySchema(
        transaction.data as string,
        ['bytes32[]', 'bytes[]'],
        web3
      )

      for (const [, schema] of Object.entries(schemaOfDataKey)) {
        const dataKeyName = schema?.name
        if (isLSP6DataKey(dataKeyName)) return true
      }
      return false
    } else if (component.functionSignature == SETDATA_ERC725Y) {
      const schemaOfDataKey = getDataKeySchema(
        transaction.data as string,
        ['bytes32', 'bytes'],
        web3
      )

      const dataKeyName = schemaOfDataKey[0]?.name
      return isLSP6DataKey(dataKeyName)
    }
  }

  return false
}

export const getDataKeySchema = (
  setDataPayload: string,
  decodingParameterTypes: ['bytes32', 'bytes'] | ['bytes32[]', 'bytes[]'],
  web3: Web3
): Record<string, ERC725JSONSchema | null> => {
  // Search through all the known schemas
  const erc725js = new ERC725([...LSP6Schema] as ERC725JSONSchema[])

  const decodedParameters = web3.eth.abi.decodeParameters(
    decodingParameterTypes,
    setDataPayload.slice(10) as string
  )

  const dataKeys = decodedParameters[0]
  return erc725js.getSchema(dataKeys)
}

/**
 * Checks if this is a transaction is of type plain value transfer.
 *
 * @param TransactionComponent[] transaction components
 * @returns boolean
 */
export const isPlainValueTransaction = (
  components?: TransactionComponent[]
): boolean => {
  if (components && components.length > 0) {
    return (
      components.length === 1 &&
      components[0].component === DefaultTxComponents.VALUE
    )
  }
  return false
}

/**
 * Checks if this is a transaction is of type plain smart contract interaction.
 *
 * @param TransactionComponent[] transaction components
 * @returns boolean
 */
export const isPlainSmartContractTransaction = (
  components?: TransactionComponent[]
): boolean => {
  if (components) {
    for (const component of components) {
      if (component.component === DefaultTxComponents.VALUE) {
        return false
      }
    }
  }
  return !!(components && components.length > 0)
}

/**
 * Checks if a transaction is of type contract create.
 *
 * @param TransactionComponent[] transaction components
 * @returns boolean
 */
export const isContractCreateTransaction = (
  components?: TransactionComponent[]
): boolean => {
  if (components && components.length > 0) {
    return components[0].component === ContractCreateComponent.component
  }
  return false
}

/**
 * Checks if a transaction is of type value received.
 *
 * @param transaction - transaction
 * @returns boolean
 */
export const isValueReceived = (
  components?: TransactionComponent[]
): boolean => {
  if (components && components.length === 1) {
    return components[0].component === DefaultTxComponents.VALUE
  }
  return false
}

/**
 * Checks if a transaction indicates that an NFT was received.
 *
 * @param transaction - The transaction object to check.
 * @returns `true` if the transaction contains an event indicating that an NFT was received, `false` otherwise.
 */
export const isNFTReceived = (transaction: Transaction): boolean => {
  const contractEvents = transaction.contractEvents
  if (contractEvents && contractEvents.length > 0) {
    for (const event of contractEvents) {
      if (event.event === ContractEventType.UNIVERSAL_RECEIVER) {
        return (
          event.returnValues['typeId'] ===
          LSP1_TYPE_IDS.LSP8Tokens_RecipientNotification
        )
      }
    }
  }
  return false
}

/**
 * Checks if a transaction involves receiving tokens.
 *
 * This function examines the contract events associated with a transaction
 * to determine if any of the events indicate that tokens were received.
 *
 * @param transaction - The transaction object to be checked.
 * @returns `true` if the transaction involves receiving tokens, `false` otherwise.
 */
export const isTokenReceived = (transaction: Transaction): boolean => {
  const contractEvents = transaction.contractEvents
  if (contractEvents && contractEvents.length > 0) {
    for (const event of contractEvents) {
      if (event.event === ContractEventType.UNIVERSAL_RECEIVER) {
        return (
          event.returnValues['typeId'] ===
          LSP1_TYPE_IDS.LSP7Tokens_RecipientNotification
        )
      }
    }
  }
  return false
}

/**
 * Checks if a transaction is likely of type contract deployment.
 *
 * @param transaction - transaction
 * @returns boolean
 */
export const isContractDeployment = (transaction: Transaction): boolean => {
  return (
    (!transaction?.to || transaction?.to === ZERO_ADDRESS) &&
    !!transaction?.data &&
    transaction.data !== '0x'
  )
}

/**
 * Return color text for transaction status.
 *
 * @param status - transaction status
 * @returns - color text
 */
export const statusColor = (status?: TransactionStatus): string => {
  let color = 'info'

  if (status) {
    ;({
      COMPLETED: () => {
        color = 'success'
      },
      FAILED: () => {
        color = 'danger'
      },
      PENDING: () => {
        color = 'warning'
      },
      INITIALIZED: () => {
        color = 'warning'
      },
    })[status]()
  }

  return color
}

/**
 * Check if transaction is executed by relayer service.
 *
 * @param transaction - transaction
 * @returns boolean
 */
export const isRelayerTransaction = (transaction: Transaction): boolean => {
  return transaction.gasProvider?.type === 'relayer'
}
